import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import { push_undo } from 'data/lib/undo'
import { diff_obj } from 'data/lib/diff'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import notificationService from "data/notification"
import { PromiseWatch } from "./lib/promise-helpers"
import validationService from "./validate"

export type Portfolio = {
  portfolioid: number
}
// private module logic //

const id: string = "portfolio"
const initialState: ApiResult<Portfolio[]> = { id, state: "init", value: [] }
const portfolioStream = new BehaviorSubject<ApiResult<Portfolio[]>>(initialState)
const portfolioEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let portfolioMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

const current = () => portfolioStream.getValue()

function sortData(data: Portfolio[]): Portfolio[] {
  data.sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code")));
  return data;
}

function clear(): void {
  portfolioStream.next({ id, state: 'ok', value: [] })
  portfolioMap = {}
}

function get(portfolioMapId: number): Portfolio {
  return portfolioMap[portfolioMapId]
}

function fetchPortfolios(noclear = false): Promise<Portfolio[] | { message: string }> {
  noclear || clear()
  portfolioStream.next({ id, state: 'busy', value: portfolioStream.getValue().value })
  let url = `portfolio`
  return Client.get(url)
  .then((records: Portfolio[]) => {
    portfolioStream.next({ id, state: 'ok', value: sortData(records) })
    validationService.fetchAll('portfolio')
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function fetch(portfolioid: number): Promise<Portfolio | { message: string }> {
  let url = `portfolio/${portfolioid}`
  return Client.get(url)
  .then((portfolio: Portfolio) => {
    validationService.fetch('portfolio', portfolioid)
    return portfolio
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function create(): Promise<Portfolio | { message: string }> {
  const streamVal = portfolioStream.getValue().value
  portfolioStream.next({ id, state: 'busy', value: streamVal })

  const payload = {
    systeminput: null
  }

  return Client.post(`portfolio`, payload)
    .then((portfolio: Portfolio) => {
      push_undo({
        undofunc: () => deleteOne(portfolio.portfolioid),
        redofunc: () => create()
      })
      portfolioMap[portfolio.portfolioid] = portfolio
      // merge results unique by portfolioid
      const streamVal = portfolioStream.getValue().value
      portfolioStream.next({
        id,
        state: 'ok',
        value: _.unionBy([portfolio], streamVal, 'portfolioid')
      })
      portfolioEventStream.next({ type: 'PORTFOLIO_CREATED', payload: portfolio })
      validationService.fetchAll('portfolio')
      return portfolio
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      portfolioStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
}

function copy(portfolioid:number, code:string) {
  const streamVal = portfolioStream.getValue().value;
  portfolioStream.next({ id, state: 'busy', value: streamVal });

  const payload = {table: 'portfolio', recordid: portfolioid, _synchronous: true, code: incrementUnique(code, "code", streamVal)};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then(async (portfolios: Portfolio[]) => {
      const newRecord = portfolios[0]
      push_undo({
        undofunc: () => deleteOne(newRecord.portfolioid),
        redofunc: () => copy(portfolioid, code)
      });
      await fetchPortfolios(true)
      portfolioEventStream.next({ type: 'PORTFOLIO_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      portfolioStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      return err
    })
}

function copies(portfolioid:number, count: number) {
  const streamVal = portfolioStream.getValue().value;
  portfolioStream.next({ id, state: 'busy', value: streamVal });

  const payload = {table: 'portfolio', recordid: portfolioid, _synchronous: true, unique: true, count};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then(async (portfolios: Portfolio[]) => {
      push_undo({
        undofunc: () => deleteMultiple(_.map(portfolios, 'portfolioid')),
        redofunc: () => copies(portfolioid, count)
      });
      await fetchPortfolios(true)
      portfolioEventStream.next({ type: 'PORTFOLIO_COPIED', payload: portfolios })
      return portfolios
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      portfolioStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      return err
    })
}

async function deleteOne(
  portfolioid: number,
  force=false
): Promise<boolean | { message: string }> {
  const streamVal = portfolioStream.getValue().value

  const deleteFn = () => deleteOne(portfolioid, true)
  const refreshFn = () => fetchPortfolios()
  if (!force && !(await handleReferences('portfolio', portfolioid, {deleteFn, refreshFn, canForce: true}))) {
    return false
  }

  // filter out the deleted portfolioid
  const value = _(streamVal)
    .filter((p) => p.portfolioid !== portfolioid)
    .value()
  value.forEach((p) => (portfolioMap[p.portfolioid] = p))

  portfolioStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(portfolioid).then(() =>
  Client.del(`portfolio/${portfolioid}`)
    .then((portfolio: object) => {
      delete portfolioMap[portfolio['portfolioId']]
      push_undo({
        undofunc: () => {
          Client.post('portfolio', portfolio)
          portfolioMap[portfolio['portfolioId']] = portfolio
          portfolioStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteOne(portfolioid)
      })
      validationService.fetchAll('portfolio')
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      portfolioStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchPortfolios()
      return err
    })
  )
}

async function deleteMultiple(
  portfolioids: number[],
  force=false
): Promise<boolean | { message: string }> {
  const streamVal = portfolioStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(ids, true)
  const refreshFn = () => fetchPortfolios()
  if (!force && !(await handleReferences('portfolio', portfolioids, {deleteFn, refreshFn, canForce: true}))) {
    return false
  }

  // filter out the deleted portfolioid
  const value = _(streamVal)
    .filter((p) => !portfolioids.includes(p.portfolioid))
    .value()
  value.forEach((p) => (portfolioMap[p.portfolioid] = p))

  portfolioStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(portfolioids).then(() =>
  Promise.all(_.map(portfolioids, portfolioid => Client.del(`portfolio/${portfolioid}`)
    .then((portfolio: Portfolio) => {
      delete portfolioMap[portfolio['portfolioId']]
      push_undo({
        undofunc: () => {
          Client.post('portfolio', portfolio)
          portfolioMap[portfolio.portfolioid] = portfolio
          portfolioStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteMultiple(portfolioids)
      })
      validationService.fetchAll('portfolio')
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      portfolioStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchPortfolios()
      return err
    })
  ))).then(trues => true)
}

function patch(portfolio: {
  portfolioid: number
}): Promise<Portfolio | { message: string }> {
  const streamVal = portfolioStream.getValue().value
  const portfolioid: number = portfolio.portfolioid
  const streamValportfolio = _.find(streamVal, { portfolioid })
  const changeset = diff_obj(portfolio, streamValportfolio)
  if (_.isEmpty(changeset)) return new Promise(() => streamVal)
  push_undo({
    undofunc: () => patch(streamValportfolio),
    redofunc: () => patch(portfolio)
  })

  let newStreamVal = _.cloneDeep(streamVal)
  newStreamVal.splice(_.findIndex(streamVal, a => a.portfolioid === portfolioid), 1, portfolio)

  // optimistic update
  portfolioStream.next({
    id,
    state: 'ok',
    value: _.cloneDeep(newStreamVal)
  })

  return _patches_.watch(portfolioid, Client.patch(`portfolio/${portfolioid}`, changeset)
    .then((portfolio: {portfolioid: number}) => {
      portfolioMap[portfolio.portfolioid] = portfolio
      if ('code' in changeset) validationService.fetchAll('portfolio')
      else validationService.fetch('portfolio', portfolioid)

      return portfolio
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      portfolioStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchPortfolios()
      return err
    }))

}

// public module interface //

const portfolioService = {
  stream: () => portfolioStream.asObservable(),
  value: () => portfolioStream.getValue().value,
  eventStream: () => portfolioEventStream.asObservable(),
  clear,
  get,
  fetch,
  fetchPortfolios,
  create,
  copy,
  copies,
  deleteOne,
  deleteMultiple,
  patch
}

export default portfolioService